/**
 * GIWAHS CDN URL Layer
 * ---------------------------------------------------------------------
 * Single source of truth for transforming any image source URL into the
 * correct CDN-served, size-appropriate, format-optimized variant.
 *
 * Migration strategy:
 *   - During MVP, images are served from external hosts (Pexels) and the
 *     Emergent static CDN. We transform their URLs at the source URL's
 *     own image-resizing API (Pexels supports ?auto=compress&w=...&h=...).
 *   - When self-hosted CDN (Cloudflare Images, Supabase Storage, Bunny CDN,
 *     ImageKit, etc.) is configured via REACT_APP_CDN_BASE, images are
 *     rewritten to <base>/<path>?w=...&h=...&q=...&fm=webp.
 *   - The component layer (Img.jsx, SEO.jsx) never needs to know which
 *     mode is active. It only calls cdnUrl() or ogImage().
 *
 * Variants are semantic, not pixel-perfect. The component layer picks the
 * variant by context, not by hardcoded width.
 */

const CDN_BASE = (process.env.REACT_APP_CDN_BASE || '').replace(/\/+$/, '');
const DEFAULT_QUALITY = 78;

/**
 * Variant presets. Mobile-first: each variant defines the base width and
 * the @2x retina variant for srcset. Aspect ratios are NOT enforced here —
 * the component sets aspect via CSS. Width/height attrs in the markup
 * prevent CLS.
 */
export const VARIANTS = {
  og:        { w: 1200, h: 630,  fit: 'cover' },  // Open Graph + Twitter cards
  hero:      { w: 1920, h: 1080, fit: 'cover' },  // Full-bleed hero backgrounds
  heroNarrow:{ w: 1200, h: 800,  fit: 'cover' },  // Article / guide covers
  card:      { w: 800,  h: 1000, fit: 'cover' },  // Supplier & product cards
  thumb:     { w: 400,  h: 500,  fit: 'cover' },  // Gallery thumbs, related-item grids
  square:    { w: 600,  h: 600,  fit: 'cover' },  // 1:1 imagery
  avatar:    { w: 200,  h: 200,  fit: 'cover' },  // small chips/avatars
};

/**
 * Returns a transformed image URL.
 * @param {string} src - Original source URL (Pexels / self-host / CDN)
 * @param {object} opts - { w, h, q, fit, format }
 * @returns {string}
 */
export const cdnUrl = (src, opts = {}) => {
  if (!src) return '';
  const { w, h, q = DEFAULT_QUALITY, fit = 'cover', format } = opts;

  // Mode 1: self-hosted CDN configured → rewrite to CDN base.
  // Assumes Cloudflare Images / Imgix / Bunny CDN-style query params.
  if (CDN_BASE && src.startsWith(CDN_BASE)) {
    return appendParams(src, { w, h, q, fit, fm: format });
  }
  if (CDN_BASE && (src.startsWith('http://') || src.startsWith('https://'))) {
    // External image proxied through CDN
    const proxied = `${CDN_BASE}/proxy?url=${encodeURIComponent(src)}`;
    return appendParams(proxied, { w, h, q, fit, fm: format });
  }
  if (CDN_BASE && src.startsWith('/')) {
    // Relative path → served from CDN
    return appendParams(`${CDN_BASE}${src}`, { w, h, q, fit, fm: format });
  }

  // Mode 2: no self-hosted CDN yet → use source-image-host's own resizer.
  if (src.includes('images.pexels.com')) {
    // Pexels API: ?auto=compress&cs=tinysrgb&w=...&h=...&dpr=1&fit=crop
    return appendParams(stripQuery(src), {
      auto: 'compress',
      cs: 'tinysrgb',
      w,
      h,
      fit: fit === 'cover' ? 'crop' : fit,
      dpr: 1,
    });
  }
  if (src.includes('images.unsplash.com')) {
    // Unsplash API: ?auto=format&fit=crop&w=...&h=...&q=...
    return appendParams(stripQuery(src), {
      auto: 'format',
      fit: fit === 'cover' ? 'crop' : fit,
      w,
      h,
      q,
    });
  }
  // Emergent static CDN and unknown hosts → pass through.
  return src;
};

/**
 * Returns a 1200×630 Open Graph variant for any source URL.
 * Used by SEO.jsx for og:image and twitter:image.
 */
export const ogImage = (src) => cdnUrl(src, VARIANTS.og);

/**
 * Build a srcset string for a given variant. Produces 1x + 2x entries.
 * Most modern browsers select the cheapest entry that fits the displayed
 * size, so 1x/2x is the right balance vs. shipping many breakpoints.
 */
export const buildSrcSet = (src, variantKey) => {
  const v = VARIANTS[variantKey];
  if (!v) return cdnUrl(src);
  const x1 = cdnUrl(src, v);
  const x2 = cdnUrl(src, { ...v, w: v.w * 2, h: v.h * 2 });
  return `${x1} 1x, ${x2} 2x`;
};

/**
 * Returns variant dimensions — used by <Img> to set width/height attrs.
 */
export const getVariantDimensions = (variantKey) => {
  const v = VARIANTS[variantKey];
  if (!v) return { width: undefined, height: undefined };
  return { width: v.w, height: v.h };
};

// ─── helpers ───────────────────────────────────────────────────────────
const stripQuery = (url) => url.split('?')[0];

const appendParams = (url, params) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return url;
  const qs = entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return `${url}${url.includes('?') ? '&' : '?'}${qs}`;
};

export const isCdnEnabled = () => Boolean(CDN_BASE);
